import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { SparklesIcon, XCircleIcon, TrophyIcon } from 'lucide-react';
export function SalesLetterAudience() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const forYouIf = ['Sientes que has avanzado… pero sigues repitiendo patrones.', 'Te cuesta sostener cambios (empiezas fuerte y luego te apagas).', 'Vives atrapado en tu diálogo interno (crítica, culpa, miedo, comparación).', 'Quieres una espiritualidad práctica, aplicable, sin humo.', 'Estás listo para dejar de reaccionar… y comenzar a elegir.'];
  const tiredOf = ['Intentar arreglar tu vida "solo con ganas", sin método', 'Acumular información sin transformación'];
  const achievements = ['Identificar creencias limitantes que dirigen tus decisiones', 'Reprogramar tu diálogo interno para que sea un aliado', 'Aplicar las 7 Leyes a tu vida real (no a teoría)', 'Sostener hábitos con consistencia (sin depender de motivación)', 'Activar tu intuición como guía clara para decidir y actuar'];
  return <section ref={ref} className="w-full bg-gradient-to-b from-[#2d1b69] via-[#1a1a4e] to-[#0a0e27] py-20 px-4 md:py-28" data-id="element-1">
      <div className="max-w-5xl mx-auto" data-id="element-2">
        {/* For You If Section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="mb-16" data-id="element-3">
          <div className="flex items-center justify-center gap-3 mb-8" data-id="element-4">
            <SparklesIcon className="w-8 h-8 text-[#d4a853]" data-id="element-5" />
            <h2 className="font-playfair text-2xl md:text-3xl lg:text-4xl text-[#d4a853]" data-id="element-6">
              Esto es para ti si…
            </h2>
          </div>

          <div className="space-y-4 max-w-3xl mx-auto" data-id="element-7">
            {forYouIf.map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            x: -20
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.4,
            delay: 0.1 + index * 0.1
          }} className="flex items-start gap-4" data-id="element-8">
                <div className="w-2 h-2 rounded-full bg-[#d4a853] mt-2.5 flex-shrink-0" data-id="element-9" />
                <p className="font-dm-sans text-lg text-[#f5f0e8]/90" data-id="element-10">{item}</p>
              </motion.div>)}
          </div>
        </motion.div>

        {/* Tired Of Section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="mb-16" data-id="element-11">
          <div className="flex items-center justify-center gap-3 mb-8" data-id="element-12">
            <XCircleIcon className="w-7 h-7 text-[#f5f0e8]/60" data-id="element-13" />
            <h3 className="font-playfair text-xl md:text-2xl text-[#f5f0e8]/80" data-id="element-14">
              Y también si estás cansado de:
            </h3>
          </div>

          <div className="space-y-3 max-w-2xl mx-auto" data-id="element-15">
            {tiredOf.map((item, index) => <motion.div key={index} initial={{
            opacity: 0
          }} animate={isInView ? {
            opacity: 1
          } : {}} transition={{
            duration: 0.4,
            delay: 0.5 + index * 0.1
          }} className="flex items-start gap-4 bg-[#0a0e27]/30 rounded-lg p-4" data-id="element-16">
                <XCircleIcon className="w-5 h-5 text-red-400/70 mt-0.5 flex-shrink-0" data-id="element-17" />
                <p className="font-dm-sans text-[#f5f0e8]/80" data-id="element-18">{item}</p>
              </motion.div>)}
          </div>
        </motion.div>

        {/* Results Section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.4
      }} data-id="element-19">
          <div className="flex items-center justify-center gap-3 mb-8" data-id="element-20">
            <TrophyIcon className="w-8 h-8 text-[#f0c75e]" data-id="element-21" />
            <h2 className="font-playfair text-2xl md:text-3xl lg:text-4xl text-[#f0c75e]" data-id="element-22">
              ¿Qué vas a lograr si lo aplicas?
            </h2>
          </div>

          <p className="font-dm-sans text-lg text-[#f5f0e8]/80 text-center mb-8" data-id="element-23">
            Vas a poder:
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto mb-10" data-id="element-24">
            {achievements.map((achievement, index) => <motion.div key={index} initial={{
            opacity: 0,
            scale: 0.95
          }} animate={isInView ? {
            opacity: 1,
            scale: 1
          } : {}} transition={{
            duration: 0.4,
            delay: 0.6 + index * 0.1
          }} className="bg-gradient-to-br from-[#d4a853]/10 to-[#d4a853]/5 border border-[#d4a853]/20 rounded-xl p-5" data-id="element-25">
                <p className="font-dm-sans text-[#f5f0e8]/90" data-id="element-26">{achievement}</p>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0
        }} animate={isInView ? {
          opacity: 1
        } : {}} transition={{
          duration: 0.6,
          delay: 1
        }} className="text-center" data-id="element-27">
            <div className="w-24 h-1 bg-gradient-to-r from-transparent via-[#d4a853] to-transparent mx-auto mb-6" data-id="element-28" />
            <p className="font-playfair text-xl md:text-2xl text-[#f5f0e8] italic max-w-2xl mx-auto" data-id="element-29">
              "No se trata de convertirte en otra persona. Se trata de{' '}
              <span className="text-[#d4a853]" data-id="element-30">volver a ti</span>: con más
              coherencia, paz y dirección."
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}